'use client'

import { Spacer, Text } from '@vinted/web-ui'
import { useIntl } from 'react-intl'

import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'
import { ProductItemModel } from '@marketplace-web/item-box/product-item-data'
import { formatCurrencyAmount } from '@marketplace-web/currency/currency-data'
import { BundleSummaryModel } from '@marketplace-web/bundles/items-bundle-data'

import BundleFooterPriceBreakdown from './BundleFooterPriceBreakdown'
import BundleFooterPricingSkeleton from './BundleFooterPricingSkeleton'

type Props = {
  isSellerBusiness: boolean
  bundleSummary: BundleSummaryModel | undefined
  selectedItems: Array<ProductItemModel>
  isBundleSummaryLoading: boolean
  escrowNoteTranslationKey?: string
}

const BundleFooterPricing = ({
  bundleSummary,
  isSellerBusiness,
  selectedItems,
  isBundleSummaryLoading,
  escrowNoteTranslationKey,
}: Props) => {
  const { locale } = useIntl()
  const unifiedPriceBreakDownTest = useAbTest('unified_price_breakdown_v2')
  const shouldShowDetailedBreakdown = unifiedPriceBreakDownTest?.variant === 'on'

  if (isBundleSummaryLoading && shouldShowDetailedBreakdown) {
    return <BundleFooterPricingSkeleton />
  }

  if (!bundleSummary) return null

  const { price, discountApplied, fullPrice } = bundleSummary

  return (
    <div className="u-flexbox u-flex-direction-column u-flex-none">
      <div className="u-flexbox u-flex-direction-row">
        {discountApplied && (
          <>
            <Text
              as="h3"
              text={formatCurrencyAmount(price, locale)}
              type={Text.Type.Subtitle}
              theme="muted"
              testId="bundle-footer-discounted-price"
            />
            <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Small} />
          </>
        )}
        <Text
          as="h3"
          text={formatCurrencyAmount(fullPrice, locale)}
          type={Text.Type.Subtitle}
          theme="muted"
          strikethrough={discountApplied}
          testId="bundle-footer-full-price"
        />
      </div>
      <BundleFooterPriceBreakdown
        bundleSummary={bundleSummary}
        isSellerBusiness={isSellerBusiness}
        selectedItems={selectedItems}
        escrowNoteTranslationKey={escrowNoteTranslationKey}
      />
    </div>
  )
}

export default BundleFooterPricing
